import React from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { showModal } from 'modal.react.js';
import { mergeDeepRight } from 'ramda';
import { useForm, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { areaLoadTypes } from 'components/Pages/Admin/Itinerary/Details/DetailsChronologicalMode/constants';
import Preview from 'components/Pages/Admin/shared/Preview';
import area from 'components/shared/GlobalModalArea/area';
import Modal from 'components/shared/GlobalModalArea/Modal';
import { ETripEvent } from 'detailsv2/structure';
import { itineraryScheduleTypeSelector } from 'store/itinerariesItem/selectors';
import { createOptionFromPageV2 } from 'utils/createOptionFromPage';

import { getOptionsLoader } from '../../../../functions';
import { useTripEventDefaultValues } from '../../../shared';
import { useAreaWizardContext } from '../../context';
import * as types from './types';
import { FieldValues } from './types';
import { areaValidationSchema } from './validation';

export const useAreaForm = () => {
  const { tripEvent, data, onSubmit } = useAreaWizardContext();

  const organizedBy = useSelector(itineraryScheduleTypeSelector);
  const defaultValues = mergeDeepRight(tripEvent, data.default);
  const defaultAttachedPage = defaultValues?.attachedPages?.[0];

  const methods = useForm<types.FieldValues>({
    defaultValues: {
      ...defaultValues,
      attachedPage: defaultAttachedPage,
    },
    resolver: yupResolver(areaValidationSchema(organizedBy)),
    mode: 'onTouched',
  });

  const { watch, clearErrors, setValue } = methods;

  const attachedPage = watch('attachedPage');
  const title = defaultValues?.data?.title;
  const details = defaultValues?.data?.details;
  const isVisibleFormBody = !!attachedPage;

  const tripEventDeafultValuesController = useTripEventDefaultValues<FieldValues>({
    eventType: ETripEvent.Area,
    methods,
    handleSubmit: onSubmit,
    isTripEventCreated: !!tripEvent.id,
  });

  React.useEffect(() => {
    if (tripEventDeafultValuesController?.savedDefaultValues?.data) return;

    const name = attachedPage?.name || null;

    if (name && !title && !details) {
      clearErrors();

      const currentTitleValue = `Welcome to ${name}`;
      const currentDetailsValue = `Today you will arrive in ${name}`;

      setValue('data.title', currentTitleValue);
      setValue('data.details', { text: currentDetailsValue });
    }
  }, [attachedPage]);

  const loadOptions = getOptionsLoader(areaLoadTypes, createOptionFromPageV2);

  const openPagePreview = async (pageId: number) => {
    await showModal(area, (res) => (
      <Modal noPadding size="lg" isOpen toClose={res} stopMouseDownPropagation>
        <Preview pageId={pageId} />
      </Modal>
    ));
  };

  return {
    methods,
    loadOptions,
    openPagePreview,
    isVisibleFormBody,
    tripEventDeafultValuesController,
  };
};
export const useAreaFormContext = () => {
  const methods = useFormContext<types.FieldValues>();
  return methods;
};
